import moment from "moment"
import { DeliveryOptionInterface } from "../models/delivery-options"
import { GetUpsellProductsPayload } from "../models/get-upsell-products-payload"
import { AuthenticateRequest } from "../models/email-authenticate-request"
import { EmailAuthenticateResponse } from "../models/email-authenticate-response"
import { ErrorItem } from "../models/error"
import { SignupInfo } from "../models/signup-info-item"
import { AddCreditCardResponse, AddCardIntentType } from "./api-types"
import { CheckAddressPayload } from "../models/check-address-payload"
import {
  EmailVerificationConfirmRequest,
  EmailVerificationRequest,
} from "../models/email-verification"
import { SignupPayload } from "../models/sign-up-form"
import { Restaurant, RestaurantListItem } from "../models/restaurant"
import { useAxiosRequest, useAxiosRequestAnonymous } from "./request"
import { AccountResponse, AccountSessionResponse } from "../models/user"
import { InitOrderRequestData } from "../models/init-order-payload"
import { CheckoutOrder, Order, SimpleOrder } from "../models/order"
import {
  AddressListSuccessResponse,
  AddressType,
  CheckAddressSuccessResponse,
} from "../models/address"
import { RecommendedProduct } from "../models/recommended-product"
import { getModifiedOpeningHours } from "./opening-hours-service"
import { UpdateOrderRequestData } from "../models/update-order-payload"
import { ApplyBenefitOnOrderResponse } from "../models/applyBenefitOnOrderResponse"
import { getCateringSpec } from "../context/catering-context/CateringProvider"

const VENUES_PATH = process.env.GATSBY_VENUES_PATH || "venues-list-all"
const API_PREFIX = "/api/v0"

export const useAnonymousAPI = () => {
  const axiosRequestAnonymous = useAxiosRequestAnonymous()

  return {
    restaurants: {
      async list(payload: { longitude?: number; latitude?: number }) {
        let path = `${API_PREFIX}/${VENUES_PATH}?latitude=${
          payload.latitude || 55
        }&longitude=${payload.longitude || 12}`
        const data = await axiosRequestAnonymous<{
          venues: {
            items: RestaurantListItem[]
          }
        }>("GET", path, {})
        return data.venues.items
      },

      async retrieve(
        restaurantIdOrSlug: string,
        preorderTime: string | null
      ): Promise<Restaurant> {
        let path = `${API_PREFIX}/venues/${restaurantIdOrSlug}`
        if (preorderTime) {
          path += `?requested_time=${encodeURIComponent(preorderTime)}`
        }

        let data = await axiosRequestAnonymous<{ data: Restaurant }>(
          "GET",
          path
        )

        const cateringSpec = getCateringSpec(data.data.shop.id)

        // Remodelling of opening hours
        const modifiedOpeningHours = getModifiedOpeningHours(
          data.data.legacy_opening_hours.regular,
          cateringSpec
        )

        return {
          ...data.data,
          modifiedOpeningHours,
        }
      },
    },
    guest: {
      guestSignin(): Promise<AccountSessionResponse> {
        const path = `${API_PREFIX}/auth/guest`
        return axiosRequestAnonymous("POST", path)
      },
    },
    auth: {
      signin(
        payload: AuthenticateRequest
      ): Promise<{ data: EmailAuthenticateResponse }> {
        const path = `${API_PREFIX}/auth/username`
        return axiosRequestAnonymous("POST", path, payload)
      },

      requestEmailVerification(
        payload: EmailVerificationRequest
      ): Promise<string> {
        const path = `${API_PREFIX}/email_verify`
        return axiosRequestAnonymous("POST", path, payload)
      },

      confirmEmailVerification(
        payload: EmailVerificationConfirmRequest
      ): Promise<string | ErrorItem> {
        const path = `${API_PREFIX}/email_confirm`
        return axiosRequestAnonymous("POST", path, payload)
      },

      signup(payload: SignupPayload): Promise<EmailAuthenticateResponse> {
        const path = `${API_PREFIX}/signup`
        return axiosRequestAnonymous("POST", path, payload)
      },

      resetPassword(payload: { email: string }): Promise<string | ErrorItem> {
        const path = `${API_PREFIX}/password-recovery/reset`
        return axiosRequestAnonymous("POST", path, payload)
      },

      signupInfo(): Promise<SignupInfo> {
        const path = `${API_PREFIX}/signup-info`
        return axiosRequestAnonymous("GET", path)
      },
    },
    checkout: {
      retrieveOrderByDisplayId(displayId: string) {
        const path = `${API_PREFIX}/tracking-orders/${displayId}`

        return axiosRequestAnonymous<{ data: SimpleOrder }>("GET", path)
      },
    },
    other: {
      // TODO
      async getCountryCode(): Promise<string> {
        const getThreeLetterCountryCode = (twoLetter: string) => {
          if (twoLetter === "NO") {
            return "nor"
          }
          if (twoLetter === "IS") {
            return "isl"
          }
          return "dnk"
        }

        const path = `${API_PREFIX}/signup-info`
        try {
          const response = await axiosRequestAnonymous<any>("GET", path)
          const signup_info = response?.data?.signup_info
          const twoLetter = signup_info && signup_info[0]?.country_code
          const threeLetterCountryCode = getThreeLetterCountryCode(twoLetter)
          return threeLetterCountryCode
        } catch {
          return "dnk"
        }
      },
    },
    passwordRecovery: {
      resetPassword({
        organizationKeyName,
        password,
        token,
      }: {
        password: string
        token: string
        organizationKeyName: string
      }) {
        const url = `/api/external/user/${organizationKeyName}/password-recovery/change`
        return axiosRequestAnonymous(
          "POST",
          url,
          { password, token },
          {
            "Content-Type": "application/json",
          }
        )
      },

      validateToken({
        token,
        organizationKeyName,
      }: {
        token: string
        organizationKeyName: string
      }) {
        const url = `/api/external/user/${organizationKeyName}/password-recovery/validate`
        return axiosRequestAnonymous(
          "POST",
          url,
          { token },
          {
            "Content-Type": "application/json",
          }
        )
      },
    },
  }
}

export const useAPI = () => {
  const axiosRequest = useAxiosRequest()

  return {
    restaurants: {
      async retrieveTimeslotOptions(
        addressUrl: string,
        timeslots_for: moment.Moment
      ) {
        // API returns all values if the timeslots_for param exists - so append it only if some other day
        if (!timeslots_for.isSame(moment(), "day")) {
          addressUrl =
            addressUrl +
            `?timeslots_for=${window.encodeURIComponent(
              timeslots_for.format("YYYY-MM-DDT00:00:00Z")
            )}`
        }
        const data = await axiosRequest("GET", addressUrl) // TODO axiosRequest return typings
        const deliveryOptionsArray: DeliveryOptionInterface[] =
          data["delivery-options"]

        return deliveryOptionsArray
      },
    },
    checkout: {
      init({ path, payload }: InitOrderRequestData): Promise<CheckoutOrder> {
        return axiosRequest("POST", path, payload)
      },

      updateAddress(addressUrl: any) {
        return axiosRequest("POST", addressUrl)
      },

      updateOrder({
        payload,
        path,
      }: UpdateOrderRequestData): Promise<CheckoutOrder> {
        return axiosRequest("POST", path, payload)
      },

      pay(paymentMethod: any, order: CheckoutOrder | undefined) {
        const payUrl = order?.order?._links?.pay
        if (!payUrl) {
          return
        }
        return axiosRequest("POST", payUrl, paymentMethod)
      },

      completeSca(order: Order) {
        const path = order.intents![0]._links.confirm! // TODO links type check
        return axiosRequest<CheckoutOrder>("POST", path)
      },

      retrieveOrderById(orderId: any) {
        if (!orderId) {
          return
        }
        let path = `${API_PREFIX}/orders/${orderId}`
        return axiosRequest<CheckoutOrder>("GET", path)
      },

      retrieveOrder(order: Order): Promise<CheckoutOrder> {
        let path = order._links.self
        return axiosRequest("GET", path)
      },

      removeFromOrder(path: string) {
        return axiosRequest("DELETE", path)
      },

      applyVoucher(path: string) {
        return axiosRequest("PUT", path)
      },

      removeVoucherOrMembercard(path: string) {
        return axiosRequest("DELETE", path)
      },

      removeGiftCard(path: string) {
        return axiosRequest("DELETE", path)
      },

      applyBenefitOnOrder({
        code,
        orderId,
      }: {
        code: string
        orderId: string
      }) {
        const path = `${API_PREFIX}/orders/${orderId}/redeem-code`
        return axiosRequest<ApplyBenefitOnOrderResponse>("POST", path, { code })
      },
    },
    addresses: {
      list(): Promise<AddressListSuccessResponse> {
        let path = `${API_PREFIX}/me/addresses`
        return axiosRequest("GET", path)
      },

      create(payload: AddressType) {
        let path = `${API_PREFIX}/me/addresses`
        return axiosRequest("POST", path, payload)
      },

      check({
        path,
        address,
        preOrderFor,
      }: CheckAddressPayload): Promise<CheckAddressSuccessResponse> {
        return axiosRequest("POST", path, {
          address_line: address.address_line,
          city: address.city,
          country_code: address.country_code,
          postcode: address.postcode,
          longitude: address.longitude,
          latitude: address.latitude,
          pre_order_for: preOrderFor,
        })
      },
      async delete(id: string): Promise<boolean> {
        const path = `${API_PREFIX}/me/addresses/${id}`
        await axiosRequest("DELETE", path)
        return true
      },
    },
    payments: {
      cancel(cancelPath: string) {
        return axiosRequest<CheckoutOrder>("POST", cancelPath)
      },

      createIntent(): Promise<AddCardIntentType> {
        const path = `${API_PREFIX}/ps/create-intent`
        return axiosRequest("POST", path, {
          accept_url: "",
          cancel_url: "",
        })
      },

      addCreditCard(intentId: string): Promise<AddCreditCardResponse> {
        const path = `${API_PREFIX}/ps/creditcard-add`
        return axiosRequest("POST", path, {
          gateway: "stripe", // addCreditCard always called with Stripe
          intent_id: intentId,
        })
      },

      deleteCreditCard(path: string) {
        return axiosRequest("DELETE", path)
      },
    },

    auth: {
      me(): Promise<AccountResponse> {
        return axiosRequest("GET", `${API_PREFIX}/me`)
      },

      signout(): Promise<string | ErrorItem> {
        const path = `${API_PREFIX}/signout`
        return axiosRequest("POST", path)
      },
    },

    recommendedProducts: {
      async getUpsellProducts(
        payload: GetUpsellProductsPayload
      ): Promise<RecommendedProduct[]> {
        const { shopId, ...rest } = payload
        const path = `${API_PREFIX}/shops/${shopId}/recommended-products`
        const data = await axiosRequest("POST", path, rest)
        return data["recommended-products"]
      },
    },
  }
}
